import axios from "axios";
import { defineStore } from "pinia";
import { useAppState } from "../Stores/AppState";
import { useWorkspace } from "../Stores/Workspaces";
import router from "../router";
import { ROUTE_NAMES } from "../Constants/routes";

import TwilioFactory from "../Utilities/TwilioClientObserver";
import { useConversation } from "../Stores/Conversation";
import { cloneDeep } from "lodash";
import { useTeamPanel } from "./TeamPanel";
import { useUserDataKeyStore } from "./UserDataKey.js";
import Utilities from "Admin/js/Helpers/utilities.js";
import { useWorkstationConnectStore } from "Admin/js/Stores/WorkstationConnect.js";
import { useRolesAndPermissions } from "Admin/js/Stores/RolesAndPermissions.js";
import { useAlignStore } from "Admin/js/Stores/Align.js";
import { useColorTheme } from "Admin/js/Composables/useColorTheme.js";
import { useWorkspaceTree } from "./WorkspaceTree";

export const useUserStore = defineStore("UserStore", {
    state: () => ({
        user: {
            id: "",
            name: "",
            email: "",
            country: "",
        },
        profile: {
            id: "",
            name: "",
            email: "",
        },
        user_data: null,
        newInvitedMember: false,
        newAssignMember: false,
        users: [],
        own_profiles: [],
        profiles: [],
        inactive_profiles: [],
        assigned_users: null,
        user_search_key: null,
        default_avatar: "/images/default-avatar.png",
        is_reinitialize_started: false,
        initiated_operations: [],
        completed_operations: [],
        initiated_progresses: [],
        completed_progresses: [],
        loadingUserDataPromise: null,
        colorTheme: useColorTheme(),
        pre_support_admin: null,
    }),
    getters: {
        is_client: (state) => state.profile?.type === "Client",
        is_not_client() {
            return !this.is_client;
        },
        isOnboardingComplete() {
            return Boolean(this.user?.signup_completed_at);
        },
        getTwilioIdentity: (state) => state.user?.twilio_identity,
        is_personal: (state) =>
            state.user_data?.profile?.organization?.type === "personal" ||
            !state.user_data?.user?.signup_completed_at,
        is_org_creator: (state) => {
            return (
                state.profile?.organization?.creator_user_id === state.user?.id
            );
        },
        is_org_admin: (state) => {
            return Boolean(state.profile?.permissions?.admin);
        },
        isOwner: (state) => {
            return Boolean(state.profile?.permissions?.owner);
        },
        isOwnerOrAdmin: (state) => {
            return (
                Boolean(state.profile?.permissions?.admin) ||
                Boolean(state.profile?.permissions?.owner)
            );
        },
        isProfileSetupCompleted() {
            const profile = this.profile;
            if (!profile) {
                return false;
            }

            return !!(
                profile.first_name?.trim() &&
                profile.last_name?.trim() &&
                profile.country_id &&
                profile.avatar_url
            );
        },
        isOrganizationSetupCompleted() {
            const organization = this.profile?.organization;
            if (!organization) {
                return false;
            }

            return !!(
                organization.organization_type &&
                organization.country_id &&
                organization.timezone &&
                organization.avatar_url
            );
        },
        owner: (state) => {
            const owner_id = state.profile?.organization?.creator_user_id;
            if (!owner_id) return null;
            if (state.user?.id === owner_id) return state.user;
            return state?.users?.find((user) => user.id === owner_id);
        },
        ownerProfile: (state) => {
            if (!state.owner) return null;
            return state.profiles.find(
                (profile) => profile.user_id === state.owner.id,
            );
        },
        usersProfilesMap: (state) => {
            return state.profiles.reduce((acc, profile) => {
                acc.set(profile.id, profile);
                return acc;
            }, new Map());
        },
        usersProfilesMapByUserId: (state) => {
            return state.profiles.reduce((acc, profile) => {
                acc.set(profile.user_id, profile);
                return acc;
            }, new Map());
        },
        inactiveUserIdMap: (state) => {
            const inactiveIdsMap = new Map();
            state.profiles.forEach((profile) => {
                if (
                    profile?.is_active === 0 &&
                    profile?.is_organization_user &&
                    !profile?.is_agent
                ) {
                    inactiveIdsMap.set(profile.id, profile.id);
                    inactiveIdsMap.set(profile.agent_id, profile.agent_id);
                }
            });
            return inactiveIdsMap;
        },
        inactiveUsers: (state) => {
            return state.profiles.filter((profile) => {
                let userProfile = profile;
                if (profile?.is_agent) {
                    userProfile = state.profiles.find(
                        (p) => profile.id === p.agent_id,
                    );
                }
                return (
                    userProfile?.is_active === 0 &&
                    userProfile?.is_organization_user
                );
            });
        },
        agent: (state) => state.profile?.agent,
        agentProfilesMap: (state) => {
            return state.profiles.reduce((acc, profile) => {
                if (profile?.is_agent) {
                    acc.set(profile.id, profile);
                }
                return acc;
            }, new Map());
        },
        agentUserProfilesMap: (state) => {
            return state.profiles.reduce((acc, profile) => {
                if (!profile?.is_agent) {
                    acc.set(profile.agent_id, profile);
                }
                return acc;
            }, new Map());
        },
        agentUserProfilesMapByEmail: (state) => {
            return state.profiles.reduce((acc, profile) => {
                if (!profile?.is_agent) {
                    acc.set(profile.email, profile);
                }
                return acc;
            }, new Map());
        },
        supportAdmin: (state) => state.user_data?.support_admin,
    },
    actions: {
        filterProfilesBy(search_key = "", profile_ids_at_start = []) {
            let members = this.profiles;

            if (search_key)
                members = members.filter(
                    (member) =>
                        member?.full_name
                            ?.toLowerCase()
                            .indexOf(search_key.toLowerCase()) > -1 ||
                        member?.email
                            ?.toLowerCase()
                            .indexOf(search_key.toLowerCase()) > -1,
                );

            if (!profile_ids_at_start?.length) return members;

            profile_ids_at_start
                .filter((profile_id) => parseInt(profile_id))
                ?.forEach((profile_id) => {
                    const workspace_onwer_index = members.findIndex(
                        (profile) => profile_id == profile.id,
                    );
                    if (workspace_onwer_index > 0) {
                        const owner_profile = members.splice(
                            workspace_onwer_index,
                            1,
                        )[0];
                        members.unshift(owner_profile);
                    }
                });

            return members;
        },
        resetCheckedProfiles() {
            this.profiles.forEach((profile) => {
                profile.checked = false;
                if (profile?.checkedOrder) {
                    delete profile?.checkedOrder;
                    if (profile?.non_registered) {
                        profile.invitation_sent = true;
                    }
                }
            });
        },
        setUserSearchKey(key = "") {
            this.user_search_key = key;
        },
        async fetchUserData() {
            if (this.user_data) return this.user_data;
            // ✅ If already fetching, just await the same promise
            if (this.loadingUserDataPromise) {
                return this.loadingUserDataPromise;
            }

            const alignStore = useAlignStore();
            this.loadingUserDataPromise = (async () => {
                try {
                    const userData = await axios.get(`/user`);
                    const { profile, user, state } = userData?.data;
                    alignStore.supportAdmin = userData?.data?.support_admin;
                    if (userData?.data?.support_admin) {
                        this.pre_support_admin = userData?.data?.support_admin;
                    }
                    // Sync color theme from backend to localStorage
                    if (profile?.color_theme) {
                        this.colorTheme.saveTheme(profile.color_theme);
                    }

                    this.setUpUserData(user, profile);
                    this.setUserData(userData.data);
                    return { profile, user, state };
                } catch (error) {
                    console.error("Error fetching user data", error);
                } finally {
                    this.loadingUserDataPromise = null;
                }
            })();
            return this.loadingUserDataPromise;
        },
        async initialize(is_reinitialize = false) {
            // Prevent multiple simultaneous initialization calls
            if (this.initializationPromise && !is_reinitialize) {
                return this.initializationPromise;
            }

            // Set promise IMMEDIATELY to prevent race conditions
            this.initializationPromise = (async () => {
                const app_state_store = useAppState();

                const data = await this.fetchUserData();
                const { profile, user, state } = data;

                if (profile?.type === "personal") {
                    this.user.signup_completed_at = user.signup_completed_at =
                        null;
                }

                useAppState().forceSignUpProcessIfNotCompleted(
                    user?.signup_completed_at,
                    user?.signup_index,
                );

                if (
                    profile?.type === "business" &&
                    profile?.permissions?.owner &&
                    profile?.organization?.creator_user_id === user?.id
                ) {
                    useAppState().setControlAsSelectedModule(false);
                } else if (profile?.type === "personal") {
                    useAppState().setControlAsSelectedModule(true);
                }

                await useUserDataKeyStore().fetchUserDataKey(profile);

                //app_state_store.updateConversationState(state.is_conversation_open);
                Utilities().toggleChatPopoverWithoutUpdatingState(
                    state.is_conversation_open,
                );

                const conversation_store = useConversation();
                app_state_store.togglePageLoader(false);
                if (state?.item_view_mode === "detail") {
                    app_state_store.is_detail_mode = true;
                }

                // Execute the main initialization logic

                return Promise.all([
                    useWorkspaceTree().loadWorkspaceCategories(),
                    axios.get("/conversations"),
                ])
                    .then(async ([workspaceCategories, convData]) => {
                        if (this.is_not_client) {
                            const conversations =
                                convData.data?.conversations.map(
                                    (conversation) => {
                                        conversation.is_quick_load =
                                            state.conversation_id ===
                                            conversation.id;
                                        return conversation;
                                    },
                                );

                            conversation_store.initialize(
                                conversations,
                                is_reinitialize,
                            );
                            //conversation_store.loadGroups();
                        }
                        this.createTwilioClientIfUserAlreadyLoggedin();
                        this.setDefaultViewForClients();
                        conversation_store.loadConversationKaamfuRequests();

                        app_state_store.fetchLoggedTime();
                        await app_state_store.initializeWith(state);
                    })
                    .catch((error) => {
                        console.trace("Error on initialize");
                        if (error?.response?.status === 403)
                            app_state_store.setPermissionError(true);

                        // Clear the promise on error so it can be retried
                        this.initializationPromise = null;
                        throw error; // Re-throw so callers can handle the error
                    })
                    .finally(() => {
                        // Only clear promise on error (handled in catch block)
                        // On success, keep the promise for subsequent calls
                    });
            })();

            return this.initializationPromise;
        },

        takeUserToCorrectRoute() {
            if (!this.isOnboardingComplete) {
                router.push({ name: ROUTE_NAMES.ALIGN });
            } else {
                //TODO: we need to handle if user enter url directly in address bar(vue-router handles) default but restoring state
                //app_state_store.restoreRoute(state)
            }
        },

        setUpUserData(user, profile) {
            this.user = { ...user, country: profile?.country_id };
            this.profile = profile;
            window.user = this.user;
            window.profile = this.profile;
            localStorage.setItem("kaamfu_user_id", this.user.id);

            if (this.profile.is_support_admin) {
                useConversation().fetchOtherOrganizationAq();
            }
        },

        setUpUserProfileData(profile) {
            this.profile = profile;
            window.profile = this.profile;
            localStorage.setItem("kaamfu_profile_id", this.profile.id);

            this.createTwilioClient();
        },
        setUpconversations(conversations) {
            initializeConversations();
        },
        createTwilioClient() {
            if (!this.profile.twilio_identity)
                return console.log("no twilio identity created");

            const { subscribeWithIdentity } = TwilioFactory();
            subscribeWithIdentity(this.profile.twilio_identity);
        } /* this is to pre initialize twilio client before loading any data
        as so speed up loading conversations */,
        createTwilioClientIfUserAlreadyLoggedin() {
            const logged_in_user_id = localStorage.getItem("kaamfu_user_id");
            if (!logged_in_user_id) return;
            const { subscribeWithIdentity } = TwilioFactory();
            subscribeWithIdentity(logged_in_user_id);
        },
        async fetchUsers() {
            if (this.users?.length) return this.users;

            return axios.get("/users").then(({ data }) => {
                this.users = data;
                return data;
            });
        },
        async fetchUsersProfiles() {
            if (this.profiles?.length) return this.profiles;

            return axios.get("/user-profiles").then(({ data }) => {
                this.profiles = data;
                this.updateInactiveProfiles();
                return data;
            });
        },
        setNewInvitedMember(val) {
            this.newAssignMember = false;
            this.newInvitedMember = val;
        },
        setNewAssignMember(val) {
            this.newInvitedMember = false;
            this.newAssignMember = val;
        },
        setAssignedUsers() {
            this.assigned_users = cloneDeep(this.users);
            this.assigned_users = this.assigned_users.map((user) => {
                if (this.isUserParticipant(user)) user.is_participant = true;
                return user;
            });
        },
        isUserParticipant(user) {
            const conversation_store = useConversation();
            const participants = conversation_store?.active?.participants;

            return (
                participants?.findIndex(
                    (participant) => participant.id == user.id,
                ) >= 0
            );
        },
        isCurrentProfile(profile_id) {
            return profile_id ? this.profile.id === profile_id : false;
        },
        isAuthorizedFor(permission) {
            return Boolean(this.profile?.permissions?.[permission]);
        },
        hasAdministrativePermission() {
            return (
                this.isAuthorizedFor("owner") || this.isAuthorizedFor("admin")
            );
        },
        hasPermissionForScreenshots(user_profile_id) {
            return this.profile?.permissions?.screenshots?.includes(
                user_profile_id,
            );
        },
        hasOnlySubordinateTimerAQEvents(user_profile_id) {
            return this.profile?.permissions?.immediate_subordinate_aq?.includes(
                user_profile_id,
            );
        },
        getUserProfile(profile_id) {
            return this.profiles?.find((profile) => profile.id == profile_id);
        },
        getUserProfileByTwilioId(twilio_identity) {
            return this.profiles?.find(
                (profile) => profile.twilio_identity == twilio_identity,
            );
        },
        getUserProfileIdByTwilioId(twilio_identity) {
            return this.profiles?.find(
                (profile) => profile.twilio_identity == twilio_identity,
            )?.user_profile_id;
        },
        getCurrentUserProfile() {
            return this.profile;
        },
        getCurrentUserProfileId() {
            return this.profile?.id;
        },
        getCurrentUserOrganizationId() {
            return this.profile?.organization_id;
        },
        getCurrentUserOrganizationName() {
            return this.profile?.organization?.name;
        },
        userProfileSetActiveTask(profile_id, status) {
            return;
        },
        updateAccount() {
            axios.patch("/profiles", {
                first_name: this.user.first_name,
                last_name: this.user.last_name,
                country_id: this.user?.country,
            });
        },
        async updatePartialProfile() {
            const old_profile = cloneDeep(this.profile);
            const old_display_name = old_profile.display_name;
            const old_first_name = old_profile.first_name;
            const old_last_name = old_profile.last_name;

            try {
                const { data } = await axios.patch(
                    `/profiles/${this.profile.id}`,
                    {
                        first_name: this.profile.first_name,
                        last_name: this.profile.last_name,
                        country_id: this.profile.country_id,
                        linkedin: this.profile.linkedin,
                    },
                );
                if (data?.data) {
                    const updated_profile = data.data;
                    if (updated_profile.first_name !== undefined) {
                        this.profile.first_name = updated_profile.first_name;
                    }
                    if (updated_profile.last_name !== undefined) {
                        this.profile.last_name = updated_profile.last_name;
                    }
                    if (updated_profile.country_id !== undefined) {
                        this.profile.country_id = updated_profile.country_id;
                    }
                    if (updated_profile.linkedin !== undefined) {
                        this.profile.linkedin = updated_profile.linkedin;
                    }
                    if (updated_profile.display_name !== undefined) {
                        this.profile.display_name =
                            updated_profile.display_name;
                    }
                    if (updated_profile.full_name !== undefined) {
                        this.profile.full_name = updated_profile.full_name;
                    }

                    // Update conversation participants immediately if name changed
                    const name_changed =
                        old_first_name !== updated_profile.first_name ||
                        old_last_name !== updated_profile.last_name ||
                        old_display_name !== updated_profile.display_name;

                    if (name_changed) {
                        useConversation().updateConversationParticipant(
                            this.profile,
                        );
                    }
                }
            } catch (error) {
                console.log("Error updating profile:", error);
            }
        },
        updateProfile(profile) {
            axios
                .patch(`/profiles/${profile.id}`, profile)
                .then(({ data }) => {});
        },
        updateProfileAvatar(profileId, base64) {
            return axios
                .post(`/profiles/${profileId}/avatar`, { avatar: base64 })
                .then(({ data }) => {
                    if (this.profile.id === profileId) {
                        this.profile.avatar_url = data.data.url;
                    }
                    return data;
                });
        },
        setUserData(val) {
            this.user_data = val;
            this.own_profiles = val.profiles;

            if (val.profile.type === "personal")
                useAppState().setControlAsSelectedModule(true);
        },

        async getProfileById(profile_id) {
            try {
                const { data } = await axios.get(`user-profiles/${profile_id}`);
                return data.data;
            } catch (error) {
                console.error("getProfileById failed", error);
                return {};
            }
        },

        async replaceUserProfile(newProfile) {
            let profile = this.profiles.find((p) => p.id === newProfile.id);
            if (!profile) {
                await this.fetchUsersProfiles();
                profile = this.profiles.find((p) => p.id === newProfile.id);
            }

            Object.keys(newProfile ?? {}).forEach((key) => {
                if (key !== "avatar_url") {
                    profile[key] = newProfile[key];
                }
            });

            if (Object.hasOwn(newProfile, "is_active")) {
                profile.is_active = Number(newProfile.is_active);
            }

            profile.is_login = this.isCurrentProfile(profile.id) ? 1 : 0;

            const img_url = newProfile.avatar_url ?? profile.avatar_url;
            if (img_url) {
                const img = new Image();
                img.onload = () => {
                    if (
                        this.profiles.findIndex((p) => p.id === profile.id) >= 0
                    ) {
                        this.profiles[
                            this.profiles.findIndex((p) => p.id === profile.id)
                        ].avatar_url = img_url;
                    }
                };
                img.src = img_url;
            }
        },

        async replaceProfile(new_profile) {
            console.log("new_profile", new_profile);
            let profile_index = this.profiles.findIndex(
                (profile) => profile.id === new_profile.id,
            );

            if (profile_index == -1) {
                profile_index = this.profiles.findIndex(
                    (profile) => profile.email === new_profile.email,
                );
            }

            profile.is_login = 0;
            if (this.isCurrentProfile(profile.id)) profile.is_login = 1;
            let old_profile = null;
            if (profile_index >= 0) {
                old_profile = this.profiles[profile_index];
                // merge and replace profile.

                const img = new Image();
                const img_url = profile.avatar_url ?? new_profile.avatar_url;
                img.onload = () => {
                    if (profile_index >= 0 && this.profiles[profile_index]) {
                        this.profiles[profile_index].avatar_url = img_url;

                        this.replaceCurrentProfile(
                            this.profiles[profile_index],
                        );
                    }
                };
                img.src = img_url;

                delete profile["avatar_url"];

                this.profiles.splice(profile_index, 1, {
                    ...this.profiles[profile_index],
                    ...profile,
                    ...(Object.hasOwn(new_profile, "is_active") && {
                        is_active: Number(new_profile.is_active),
                    }),
                });

                this.replaceCurrentProfile(this.profiles[profile_index]);
            } else {
                this.profiles.push(new_profile);
            }

            // If user is not active but its role is support admin. Remove profile.
            let has_support_admin_modified = false;

            if (!new_profile?.is_active && new_profile?.is_support_admin) {
                const profile_index = this.profiles.findIndex(
                    (profile) => profile.id === new_profile.id,
                );
                if (profile_index >= 0) {
                    this.profiles.splice(profile_index, 1);
                }
                this.user_data.support_admin = null;
                useConversation().removeSupportAdminDMConversation(
                    new_profile.id,
                );
                has_support_admin_modified = true;
            }

            if (
                old_profile &&
                new_profile.display_name !== old_profile.display_name
            ) {
                useConversation().updateConversationParticipant(new_profile);
            }

            if (new_profile?.is_active && new_profile.is_support_admin) {
                this.user_data.support_admin = new_profile;
                has_support_admin_modified = true;
            }

            if (useAppState().is_directory) {
                useWorkstationConnectStore().fetchWscUsers();
            }

            if (
                has_support_admin_modified &&
                (this.isOwner || this.is_org_admin)
            ) {
                useRolesAndPermissions().refetchRolePermissions();
            }
        },
        async replaceCurrentProfile(new_profile) {
            this.user_data.profile = this.profile = {
                ...this.profile,
                ...new_profile,
            };
        },

        // async addCompletedOperation(completed_operation) {
        //     const itemIndex = this.initiated_operations.findIndex(
        //         (item) => item.id === completed_operation.id,
        //     );
        //     if (itemIndex !== -1) {
        //         this.initiated_operations.splice(itemIndex, 1);
        //     }
        //
        //     this.completed_operations.push(completed_operation);
        // },

        async addCompletedOperation(completed_operation) {
            const itemIndex = this.initiated_operations.findIndex(
                (item) => item.id === completed_operation.id,
            );
            if (itemIndex !== -1) {
                this.initiated_operations.splice(itemIndex, 1);
            }

            const completedIndex = this.completed_operations.findIndex(
                (item) => item.id === completed_operation.id,
            );

            if (completedIndex !== -1) {
                this.completed_operations.splice(
                    completedIndex,
                    1,
                    completed_operation,
                ); // replace
            } else {
                this.completed_operations.push(completed_operation); // add new
            }
        },

        async addInitiatedOperation(initiated_operation) {
            this.initiated_operations.push(initiated_operation);
        },

        addProfile(new_profile) {
            const profile_index = this.profiles.findIndex(
                (profile) => profile.email === new_profile.email,
            );
            if (profile_index !== -1) {
                this.profiles.splice(profile_index, 1, new_profile);
            } else {
                this.profiles.push(new_profile);
            }
        },
        updateProfileTask(item) {
            const active_task_profile = this.profiles.find(
                (profile) => profile?.active_task?.id === item.id,
            );
            if (active_task_profile)
                active_task_profile.active_task.name = item.name;

            const paused_task_profile = this.profiles.find(
                (profile) => profile.paused_task?.id === item.id,
            );
            if (paused_task_profile)
                paused_task_profile.paused_task.name = item.name;
        },
        setDefaultViewForClients() {
            if (this.is_not_client) return;

            const workspace_store = useWorkspace();
            workspace_store.setFullMode();
        },

        isCurrentUser(twilio_identity) {
            return this.user.twilio_identity === twilio_identity;
        },

        isNotCurrentUser(twilio_identity) {
            return this.user.twilio_identity !== twilio_identity;
        },

        async reInitialize() {
            if (this.is_reinitialize_started) {
                return;
            }
            try {
                this.is_reinitialize_started = true;
                console.log("ReInitialize started at", Date().toString());
                this.initialize(true);
                // Team Panel start
                if (useTeamPanel().is_prepared) {
                    await useTeamPanel().reInitializeTeamPanel();
                }
                console.log("ReInitialize ended at", Date().toString());
                this.is_reinitialize_started = false;
                // Team Panel end
            } catch (e) {
                console.log("ReInitialize failed", e, Date().toString());
                this.is_reinitialize_started = false;
            }
        },

        increaseTwilioConversationCount() {
            this.profile.twilio_conversations_count += 1;
        },
        setTwilioConversationCount(count) {
            this.profile.twilio_conversations_count = count;
        },
        decreaseTwilioConversationCount() {
            this.profile.twilio_conversations_count -= 1;
        },
        canInviteToOrganization() {
            return this.getCurrentUserProfile().type === "business";
        },
        async loadUserState() {
            const res = await axios.get(`/user`);
            this.setUserData(res.data);
            useAppState().forceSignUpProcessIfNotCompleted(
                res.data?.user?.signup_completed_at,
            );
        },
        canShowAsigner(user_profile) {
            if (!user_profile || !this.profile?.organization?.creator_user_id) {
                return false;
            }

            if (this.profile?.organization?.creator_user_id === this.user?.id) {
                return user_profile.email !== this.user.email;
            }

            const subordinates = useTeamPanel().getSubordinatesByProfile(
                this.profile.id,
            );
            const user = subordinates.find(
                (user) => user.user_profile_id === user_profile.user_profile_id,
            );
            return !!user;
        },
        async enableAdminSupport() {
            try {
                const response = await axios.post(
                    "/organizations/invite/support-admin",
                );

                const isExist = this.profiles.find(
                    (profile) => profile.id === response.data.data.id,
                );
                if (!isExist) {
                    this.profiles.push(response.data.data);
                }
                return response.data.data;
            } catch (error) {
                Utilities().showErrorSnackbar(
                    error?.response?.data?.message ??
                        "Something went wrong. Please try again later.",
                );
                return null;
            }
        },

        async disableAdminSupport() {
            try {
                await axios.post("/organizations/un-invite/support-admin");

                const supportAdminData =
                    this.user_data.support_admin ?? this.pre_support_admin;
                this.user_data.support_admin = null;
                const isExist = this.profiles.find(
                    (profile) => profile.id === supportAdminData?.id,
                );
                if (isExist) {
                    this.profiles.splice(this.profiles.indexOf(isExist), 1);
                }
            } catch (error) {}
        },

        getProfilesByIds(profile_ids = [], preserve_sorting = false) {
            let members = this.profiles;

            if (!profile_ids?.length) return [];

            let response = members.filter((profile) =>
                profile_ids.includes(profile.id),
            );

            if (preserve_sorting) {
                response = profile_ids
                    .map((id) => response.find((profile) => profile.id === id))
                    .filter(Boolean);
            }

            return response;
        },
        updateInactiveProfiles() {
            this.inactive_profiles = this.profiles
                .filter((profile) => profile.is_active === 0)
                .map((profile) => profile.id);

            return this.inactive_profiles;
        },
        updateCurrentUserPermissions(permissions) {
            this.profile.permissions = permissions;
        },
        updateCurrentUserStatus(obj) {
            const profile = this.profiles.find(
                (profile) => profile.id === this.profile.id,
            );
            if (!profile) return;
            Object.keys(obj ?? {}).forEach((key) => {
                const value = obj[key];
                profile[key] = value;
                this.profile[key] = value;
            });
        },
        getCurrentUserStatus() {
            const profile = this.profiles.find(
                (profile) => profile.id === this.profile.id,
            );
            return profile;
        },
        findProfileById(profile_id) {
            return this.profiles.find((profile) => profile.id === profile_id);
        },
        updateUserBasicDetails(userLatestDetails) {
            this.profile = {
                ...this.profile,
                ...userLatestDetails,
            };
            this.user = {
                ...this.user,
                ...userLatestDetails,
            };
        },
        isMainKaamfuOrganization() {
            return this.user_data.is_kaamfu_organization;
        },
        removeOtherOrganizationProfile(user_profile) {
            // check if its current user profile. If so, do not remove. Its automatically handled by api middleware.
            if (user_profile.id == this.getCurrentUserProfileId()) {
                return;
            }
            const profile_index = this.own_profiles.findIndex(
                (profile) =>
                    profile.id === user_profile.id &&
                    profile.organization_id == user_profile.organization_id,
            );

            if (profile_index >= 0) {
                this.own_profiles.splice(profile_index, 1);
            }
        },
        addOtherOrganizationProfile(user_profile) {
            // User information is not shared across the organization. Therefore, better to reload all profiles in background.
            const profile_index = this.own_profiles.findIndex(
                (profile) =>
                    profile.id === user_profile.id &&
                    profile.organization_id == user_profile.organization_id,
            );

            if (profile_index == -1) {
                this.fetchUserData();
            }
        },
        async fetchImportCount(payload) {
            try {
                const response = await axios.post(
                    `/save-profiles/import-count`,
                    payload,
                );
                return response.data.data;
            } catch (error) {
                return 0;
            }
        },
        async uploadTeam(payload) {
            try {
                const response = await axios.post(
                    `/save-profiles/team`,
                    payload,
                );
                Utilities().showSuccessSnackbar(response.data.message);
                return response.data.data;
            } catch (error) {
                Utilities().showErrorSnackbar(
                    error?.response?.data?.message ??
                        "Something went wrong. Please try again later.",
                );
                return null;
            }
        },

        async addInitiatedProgress(initiated_progress) {
            console.log(
                "UserStore: addInitiatedProgress called with:",
                initiated_progress,
            );
            this.initiated_progresses.push(initiated_progress);
            console.log(
                "UserStore: initiated_progresses after add:",
                this.initiated_progresses,
            );
        },

        async addCompletedProgress(completed_progress) {
            const completedIndex = this.completed_progresses.findIndex(
                (item) => item.id === completed_progress.id,
            );

            if (completedIndex !== -1) {
                this.completed_progresses.splice(
                    completedIndex,
                    1,
                    completed_progress,
                ); // replace
            } else {
                this.completed_progresses.push(completed_progress); // add new
            }
        },

        async getInitiatedProgresses() {
            return this.initiated_progresses;
        },

        async getCompletedProgresses() {
            return this.completed_progresses;
        },

        /**
         * Hydrate initiated_progresses and completed_progresses from API (e.g. after page refresh).
         * Merges by id so existing in-session items are not duplicated.
         */
        hydrateActiveImportProgresses(initiated = [], completed = []) {
            const existingInitiatedIds = new Set(
                this.initiated_progresses.map((p) => p.id),
            );
            initiated.forEach((p) => {
                if (
                    typeof window.registerForLiveSyncOnImportProgress ===
                    "function"
                ) {
                    window.registerForLiveSyncOnImportProgress(p.id);
                }

                if (!existingInitiatedIds.has(p.id)) {
                    this.initiated_progresses.push(p);
                    existingInitiatedIds.add(p.id);
                }
            });

            const existingCompletedIds = new Set(
                this.completed_progresses.map((p) => p.id),
            );
            completed.forEach((p) => {
                if (!existingCompletedIds.has(p.id)) {
                    this.completed_progresses.push(p);
                    existingCompletedIds.add(p.id);
                }
            });
        },

        getProfileAndAgent(profile) {
            if (profile?.is_agent) {
                return {
                    user: this.getAgentUserProfileByAgentId(profile?.id),
                    agent: profile,
                };
            }
            return {
                user: profile,
                agent: this.getAgentProfileById(profile?.agent_id),
            };
        },

        getAgentProfileById(profileId) {
            return this.agentProfilesMap.get(profileId);
        },

        getAgentUserProfileByAgentId(profileId) {
            return this.agentUserProfilesMap.get(profileId);
        },
        getUserProfileById(profileId) {
            return this.usersProfilesMap.get(profileId);
        },
        isDisassociatedUser(profileId) {
            return this.inactiveUserIdMap.has(profileId);
        },
        getUserProfileByUserId(userId) {
            return this.usersProfilesMapByUserId.get(userId);
        },
    },
});
